import { useAnalytics } from "@/hooks/useAnalytics";
import Link from "next/link";
import React from "react";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children: React.ReactNode;
  href?: string;
  variant?: "default" | "inverted";
  trackingContext?: string;
  trackingMetadata?: Record<string, unknown>;
}

const Button: React.FC<ButtonProps> = ({
  children,
  className = "",
  href,
  variant = "default",
  trackingContext,
  trackingMetadata,
  ...props
}) => {
  const { logEvent } = useAnalytics();

  const baseClasses = "inline-block min-w-[165px] py-2.5 px-2 border font-raleway text-sm font-bold tracking-[0.1em] text-center cursor-pointer transition-colors duration-200";

  const variantClasses = variant === "inverted"
    ? "border-black bg-black text-white hover:bg-light-grey focus:bg-light-grey"
    : "border-black bg-white text-black hover:bg-light-grey focus:bg-light-grey";

  const buttonClasses = `${baseClasses} ${variantClasses} ${className}`;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    if (trackingContext) {
      logEvent("button_click", {
        context: trackingContext,
        href,
        button_text: children,
        variant,
        ...trackingMetadata
      });
    }

    if (props.onClick) {
      props.onClick(e as React.MouseEvent<HTMLButtonElement>);
    }
  };

  if (href) {
    return (
      <Link href={href} className={buttonClasses} onClick={handleClick}>
        {children}
      </Link>
    );
  }

  return (
    <button className={buttonClasses} {...props} onClick={handleClick}>
      {children}
    </button>
  );
};

export default Button;